import { phone as phoneLib } from '@parsina/framework';

// ─────────────────────────────────────────────────────────────────────────────
// A patient's numbers, read (PM-0536).
//
// A record holds up to five numbers — the patient's own, a parent's, a spouse's —
// and `phone` stays the main one's value. Every screen that showed `phone` shows
// the main number and says how many more there are; a search says which number it
// found the patient by when that was not the main one.
// ─────────────────────────────────────────────────────────────────────────────

/** Fewer digits than this in a search say nothing about which number matched. */
export const MATCH_MIN_DIGITS = 4;

const digitsOf = (value) => phoneLib.foldDigits(value == null ? '' : String(value)).replace(/\D/g, '');

/**
 * The record's numbers, the main one first. A patient read without `phones` — a
 * booking preset, a candidate chosen off a lookup, a record whose numbers the BFF
 * could not read — is its `phone` alone.
 */
export function patientPhones(patient) {
  const rows = Array.isArray(patient?.phones)
    ? patient.phones.filter((p) => p && (p.number || p.raw))
    : [];
  if (rows.length) {
    const primary = rows.find((p) => p.isPrimary) || rows[0];
    return [primary, ...rows.filter((p) => p !== primary)];
  }
  return patient?.phone
    ? [{ id: null, number: patient.phone, raw: patient.phone, relation: 'unknown', isPrimary: true }]
    : [];
}

/** What a stored number is shown and matched by: E.164, or as typed for a landline. */
export const phoneValue = (row) => row?.number || row?.raw || '';

/**
 * The number a search found this patient by, when it was NOT the main one; null
 * otherwise. The query's digits (any script, at least MATCH_MIN_DIGITS) must sit
 * inside the number as stored or in its national spelling — `0912…` finds
 * `+98912…`. When the main number matches too, it is the main one that answered.
 */
export function matchedOtherPhone(patient, query) {
  const wanted = digitsOf(query);
  if (wanted.length < MATCH_MIN_DIGITS) return null;
  const [primary, ...others] = patientPhones(patient);
  const hits = (row) => [row.number, row.raw, phoneLib.toNational(phoneValue(row))]
    .some((v) => digitsOf(v).includes(wanted));
  if (!primary || hits(primary)) return null;
  return others.find(hits) || null;
}
